import { useMemo, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Info } from 'lucide-react';
import { Spreadsheet } from '../../spreadsheet';
import { SheetTabs } from '../../spreadsheet/components/SheetTabs';
import { Card, CardContent } from '../../ui/card';
import { LoadingState } from '../../ui/loading-state';
import { useShifts } from '../../../lib/supabase/shifts/supabaseShiftService';
import { useModels } from '../../../lib/supabase/models/supabaseModelService';
import { calculateModelEarnings, calculateDateRanges, type ModelEarningsData } from '@shiftly/shared/src/calculators/modelEarningsCalculator';
import { CustomContextMenuItem } from '../../spreadsheet/types';
import { ModelRecurringShiftsDialog } from '../model-recurring-shifts/ModelRecurringShiftsDialog';

type SheetTab = 'uk-set-1' | 'uk-set-2' | 'fp-set-1' | 'fp-set-2';

export function ModelEarningsSpreadsheet() {
  const [isLoading, setIsLoading] = useState(true);
  const [modelEarningsData, setModelEarningsData] = useState<ModelEarningsData[]>([]);
  const [activeTab, setActiveTab] = useState<SheetTab>('uk-set-1');
  const [recurringShiftsDialog, setRecurringShiftsDialog] = useState<{
    isOpen: boolean;
    model_id: string;
    modelName: string;
  }>({ isOpen: false, model_id: '', modelName: '' });
  
  const { startDate, month2Ago, month1Ago, currentMonth } = useMemo(() => calculateDateRanges(), []);

  // Fetch all models
  const { data: models = [], isLoading: modelsLoading } = useModels();

  // Fetch shifts from the last 3 months
  const shiftsFilter = useMemo(() => {
    let endDate = DateTime.utc().set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
    const now = DateTime.utc();
    if (endDate > now) {
      endDate = endDate.minus({ days: 1 });
    }

    return [
      { field: 'start_date_time', operator: 'gte' as const, value: startDate },
      { field: 'start_date_time', operator: 'lte' as const, value: endDate },
      { field: 'submitted', operator: 'eq' as const, value: true }
    ];
  }, [startDate]);

  const { data: shifts = [], isLoading: shiftsLoading } = useShifts({
    filters: shiftsFilter
  });

  useEffect(() => {
    if (modelsLoading || shiftsLoading) {
      setIsLoading(true);
      return;
    }

    const modelData = calculateModelEarnings(shifts, models);
    setModelEarningsData(modelData);
    setIsLoading(false);
  }, [shifts, models, modelsLoading, shiftsLoading]);

  // Filter models based on active tab
  const filteredModels = useMemo(() => {
    return models.filter((model) => {
      switch (activeTab) {
        case 'uk-set-1':
          return model.pay_class === 'UK' && model.set === 1;
        case 'uk-set-2':
          return model.pay_class === 'UK' && model.set === 2;
        case 'fp-set-1':
          return model.pay_class === 'FP' && model.set === 1;
        case 'fp-set-2':
          return model.pay_class === 'FP' && model.set === 2;
        default:
          return false;
      }
    });
  }, [models, activeTab]);

  // Filter earnings data based on filtered models
  const filteredModelEarningsData = useMemo(() => {
    const filteredModelIds = new Set(filteredModels.map(model => model.id));
    return modelEarningsData.filter(data => {
      const model = models.find(m => m.name === data.modelName);
      return model && filteredModelIds.has(model.id);
    });
  }, [modelEarningsData, filteredModels, models]);

  // Create spreadsheet data
  const spreadsheetData = useMemo(() => {
    return filteredModelEarningsData.map((data) => {
      const dailyAvgPercentageChange = data.dailyAvg1MonthAgo > 0 
        ? ((data.dailyAvgThisMonth - data.dailyAvg1MonthAgo) / data.dailyAvg1MonthAgo) * 100
        : 0;
      const dailyAvgPercentageDisplay = dailyAvgPercentageChange !== 0 
        ? `(${dailyAvgPercentageChange > 0 ? '+' : ''}${dailyAvgPercentageChange.toFixed(1)}%)`
        : '';

      const formatTimeSlotDisplay = (
        prevPpv: number, 
        prevNoppv: number, 
        currPpv: number, 
        currNoppv: number
      ) => {
        const percentChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
        const percentDisplay = percentChange !== 0 ? ` (${percentChange > 0 ? '+' : ''}${percentChange.toFixed(1)}%)` : '';
        
        return `$${prevPpv.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/$${prevNoppv.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} → $${currPpv.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/$${currNoppv.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}${percentDisplay}`;
      };

      // Find the corresponding model to get model_id
      const model = models.find(m => m.name === data.modelName);

      return {
        A: data.modelName,
        B: `$${data.total2MonthsAgo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        C: `$${data.total1MonthAgo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        D: `$${data.totalThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        E: `$${data.dailyAvg1MonthAgo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        F: `$${data.dailyAvgThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dailyAvgPercentageDisplay}`,
        G: formatTimeSlotDisplay(data.prevPpvAvg6to2, data.prevNoppvAvg6to2, data.ppvAvg6to2, data.noppvAvg6to2),
        H: formatTimeSlotDisplay(data.prevPpvAvg2to10, data.prevNoppvAvg2to10, data.ppvAvg2to10, data.noppvAvg2to10),
        I: formatTimeSlotDisplay(data.prevPpvAvg10to6, data.prevNoppvAvg10to6, data.ppvAvg10to6, data.noppvAvg10to6),
        J: formatTimeSlotDisplay(data.prevPpvAvg6amTo6pm, data.prevNoppvAvg6amTo6pm, data.ppvAvg6amTo6pm, data.noppvAvg6amTo6pm),
        K: formatTimeSlotDisplay(data.prevPpvAvg6pmTo6am, data.prevNoppvAvg6pmTo6am, data.ppvAvg6pmTo6am, data.noppvAvg6pmTo6am),
        model_id: model?.id,
        modelName: data.modelName
      };
    });
  }, [filteredModelEarningsData, models]);

  // Custom context menu items
  const customContextMenuItems: CustomContextMenuItem[] = useMemo(() => {
    const items: CustomContextMenuItem[] = [];

    items.push({
      id: 'view-recurring-shifts',
      label: 'View Fixed Shifts',
      icon: <Info size={16} />,
      onClick: async (rowData, _rowIndex) => {
        if (rowData && rowData.model_id && rowData.modelName) {
          setRecurringShiftsDialog({
            isOpen: true,
            model_id: rowData.model_id,
            modelName: rowData.modelName
          });
        }
      },
      disabled: false
    });

    return items;
  }, []);

  if (isLoading) {
    return <LoadingState />;
  }

  const tabs = [
    { id: 'uk-set-1' as SheetTab, label: 'UK Set 1' },
    { id: 'uk-set-2' as SheetTab, label: 'UK Set 2' },
    { id: 'fp-set-1' as SheetTab, label: 'FP Set 1' },
    { id: 'fp-set-2' as SheetTab, label: 'FP Set 2' }
  ];

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="relative">
            <Spreadsheet
            columns={11}
            rows={Math.max(spreadsheetData.length, 20)}
            data={spreadsheetData}
            getCellStyle={(rowIndex, colIndex) => {
              if (rowIndex < filteredModelEarningsData.length) {
                const data = filteredModelEarningsData[rowIndex];
                
                if (colIndex === 'F') {
                  const percentageChange = data?.dailyAvg1MonthAgo > 0 
                    ? ((data.dailyAvgThisMonth - data.dailyAvg1MonthAgo) / data.dailyAvg1MonthAgo) * 100
                    : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                } else if (colIndex === 'G') {
                  const prevPpv = data?.prevPpvAvg6to2 || 0;
                  const currPpv = data?.ppvAvg6to2 || 0;
                  const percentageChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                } else if (colIndex === 'H') {
                  const prevPpv = data?.prevPpvAvg2to10 || 0;
                  const currPpv = data?.ppvAvg2to10 || 0;
                  const percentageChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                } else if (colIndex === 'I') {
                  const prevPpv = data?.prevPpvAvg10to6 || 0;
                  const currPpv = data?.ppvAvg10to6 || 0;
                  const percentageChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                } else if (colIndex === 'J') {
                  const prevPpv = data?.prevPpvAvg6amTo6pm || 0;
                  const currPpv = data?.ppvAvg6amTo6pm || 0;
                  const percentageChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                } else if (colIndex === 'K') {
                  const prevPpv = data?.prevPpvAvg6pmTo6am || 0;
                  const currPpv = data?.ppvAvg6pmTo6am || 0;
                  const percentageChange = prevPpv > 0 ? ((currPpv - prevPpv) / prevPpv) * 100 : 0;
                  
                  if (percentageChange > 0) {
                    return { color: '#16a34a' }; // Green for positive
                  } else if (percentageChange < 0) {
                    return { color: '#dc2626' }; // Red for negative
                  }
                }
              }
              return {};
            }}
            isCellEditable={() => false}
            dropdownOptions={{}}
            onDataChange={() => {}}
            editable={false}
            columnWidths={[150, 120, 120, 120, 120, 150, 300, 300, 300, 300, 300]}
            rowHeight={35}
            showColumnHeaders={true}
            customColumnHeaders={[
              'Model',
              `Total ${month2Ago}`,
              `Total ${month1Ago}`,
              `Total ${currentMonth}`,
              `Daily Avg ${month1Ago}`,
              `Daily Avg ${currentMonth}`,
              '6-2 8hr Avg',
              '2-10 8hr Avg',
              '10-6 8hr Avg',
              '6am-6pm 12hr Avg',
              '6pm-6am 12hr Avg'
            ]}
            fillHeight={true}
            buffers={{ fullscreen: 65, normal: 65 }}
            csvFilename={`model-earnings-report-${DateTime.utc().year}-${DateTime.utc().month.toString().padStart(2, '0')}.csv`}
            csvIncludeHeaders={true}
            customContextMenuItems={customContextMenuItems}
          />
          <SheetTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tabId) => setActiveTab(tabId as SheetTab)}
          />
          </div>
        </CardContent>
      </Card>
      
      <ModelRecurringShiftsDialog
        isOpen={recurringShiftsDialog.isOpen}
        onClose={() => setRecurringShiftsDialog({ isOpen: false, model_id: '', modelName: '' })}
        model_id={recurringShiftsDialog.model_id}
        modelName={recurringShiftsDialog.modelName}
      />
    </div>
  );
}